// components/OurMethod.js
import React, { useRef, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next'; 
import conceptionImg from '../images/ATC_conception.webp';
import realisationImg from '../images/ATC_réalisation.webp';
import livraisonImg from '../images/ATC_livraison.webp';
import './OurMethod.css';

const OurMethod = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const phases = useMemo(() => [
    {
      number: '01',
      title: t('phase1_card_Notre_methode'), // ← syntaxe t('clé')
      image: conceptionImg,
      phase: t('phase1_label') || 'CONCEPTION',
      description: t('phase1_description') || 'De l\'idée au cahier des charges',
    },
    {
      number: '02',
      title: t('phase2_card_Notre_methode'),
      image: realisationImg,
      phase: t('phase2_label') || 'RÉALISATION',
      description: t('phase2_description') || 'Ingénierie et développement',
    },
    {
      number: '03',
      title: t('phase3_card_Notre_methode'),
      image: livraisonImg,
      phase: t('phase3_label') || 'LIVRAISON',
      description: t('phase3_description') || 'Déploiement et accompagnement',
    },
  ], [t]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  const numberVariants = {
    hover: {
      scale: 1.1,
      boxShadow: '0 8px 30px rgba(0, 102, 204, 0.25)',
      transition: { duration: 0.3 },
    },
  };

  const imageVariants = {
    hover: {
      scale: 1.06,
      transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  return (
    <section className="our-method-section" ref={ref}>
      <div className="container">
        <div className="section-header">
          <motion.span
            className="section-tag"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            {t('titre_Notre_methode')}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t('sous_titre_Notre_methode')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t('paragraph_titre_Notre_methode')}
          </motion.p>
        </div>

        <motion.div
          className="method-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Ligne de connexion entre les cartes */}
          <div className="method-connector">
            <svg viewBox="0 0 100 4" preserveAspectRatio="none">
              <motion.line
                x1="0"
                y1="2"
                x2="100"
                y2="2"
                stroke="#0066cc"
                strokeWidth="2"
                strokeDasharray="6 4"
                initial={{ pathLength: 0 }}
                animate={isInView ? { pathLength: 1 } : {}}
                transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.5 }}
              />
            </svg>
          </div>

          {phases.map((phase, index) => (
            <motion.div
              key={index}
              className="method-card"
              variants={cardVariants}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <div className="method-card-inner">
                <div className="method-image-wrapper">
                  <motion.div
                    className="method-image"
                    variants={imageVariants}
                    whileHover="hover"
                  >
                    <img src={phase.image} alt={phase.title} />
                    <div className="method-image-overlay">
                      <span className="method-phase">{phase.phase}</span>
                    </div>
                  </motion.div>
                  <motion.div
                    className="method-number"
                    variants={numberVariants}
                    whileHover="hover"
                  >
                    {phase.number}
                  </motion.div>
                </div>

                <div className="method-content">
                  <motion.div
                    className="method-progress"
                    initial={{ width: 0 }}
                    animate={isInView ? { width: '100%' } : {}}
                    transition={{ duration: 0.8, delay: 0.5 + index * 0.15 }}
                  />
                  <h4>{phase.title}</h4>
                  <p className="method-description">{phase.description}</p>
                  <div className="method-step">
                    <span className="step-number">{phase.number}</span>
                    <span className="step-label">{phase.phase}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default OurMethod;